import {
  Button,
  ChevronRightIcon,
  CloseIcon,
  Input,
  MercaiOpusShortIcon,
  MoreIcon,
  showToast,
  Text,
} from '@mercai/clever'
import { FormEvent, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { formatLongDate, onConfirmDangerMessage } from '@/helpers'

import styles from './comment.module.css'
import { CommentProps } from './comment.type'

export const Comment = ({
  comment,
  hasPermissionToEditOrDelete,
  deleteComment,
  updateComment,
  invalidateQueries,
}: CommentProps) => {
  const { t: tEnums } = useTranslation('enums')
  const { t } = useTranslation('componentsComments')

  const [isActive, setIsActive] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [editingComment, setEditingComment] = useState(comment?.comment || '')

  const rootRef = useRef<HTMLDivElement>(null)

  const onEditComment = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const response = await updateComment({
      uuid: comment?.uuid || '',
      comment: editingComment,
    })

    if (!response.success) {
      return showToast({
        variant: 'error',
        title: response?.message || t('commentsEditCommentErrorDescription'),
      })
    }

    setIsEditing(false)

    invalidateQueries?.()
  }

  const onDeleteComment = async () => {
    const confirm = await onConfirmDangerMessage(
      t('commentsDeleteCommentConfirmTitle'),
      t('commentsDeleteCommentConfirmDescription'),
      t('commentsDeleteCommentConfirmButton'),
      t('commentsDeleteCommentCancelButton'),
    )

    if (!confirm) return null

    const response = await deleteComment(comment?.uuid || '')

    if (!response.success) {
      return showToast({
        variant: 'error',
        title: response?.message || t('commentsDeleteCommentErrorDescription'),
      })
    }

    invalidateQueries?.()
  }

  function removeMilliseconds(dateStr: string) {
    const date = new Date(dateStr)
    date.setMilliseconds(0)
    return date
  }

  useEffect(() => {
    const closeOpenned = (e: MouseEvent) => {
      if (
        rootRef.current &&
        isActive &&
        !rootRef.current.contains(e.target as Node)
      ) {
        setIsActive(false)
      }
    }

    document.addEventListener('mousedown', closeOpenned)

    return () => document.removeEventListener('mousedown', closeOpenned)
  }, [isActive, rootRef])

  useEffect(() => {
    if (isEditing) {
      setEditingComment(comment?.comment || '')
    }
  }, [isEditing])

  return (
    <div className={styles.container} key={comment?.uuid}>
      {!isEditing && (
        <>
          <div className={styles.content}>
            <MercaiOpusShortIcon className={styles.icon} />
            <Text variant="text-small" className={styles.name}>
              <strong>
                {comment?.user?.name || t('commentsNotInformed')}{' '}
                {comment?.type &&
                  `- ${tEnums(`order_center_comments_type_${comment?.type}`)}`}
                {comment?.subject ? ` - ${comment.subject}` : ''}
                <Text variant="text-xsmall" className={styles['gray-400']}>
                  {` - ${comment?.user?.email}`}

                  {comment?.updatedAt &&
                    removeMilliseconds(comment?.updatedAt).getTime() !==
                      removeMilliseconds(comment?.createdAt).getTime() &&
                    ` - (${t('commentsEditedComment')})`}
                </Text>
              </strong>
              <Text variant="text-small">{comment?.comment}</Text>
            </Text>
          </div>
          <div className={styles['right-content']}>
            <Text variant="text-xsmall" className={styles['gray-400']}>
              {formatLongDate(comment?.createdAt || '')}
            </Text>
            {hasPermissionToEditOrDelete && (
              <div ref={rootRef} className={styles['actions-button']}>
                <MoreIcon onClick={() => setIsActive(!isActive)} />
                {isActive && (
                  <div className={styles['actions-container']}>
                    <div className={styles['actions-content']}>
                      <Text
                        variant="text-small"
                        className={styles.action}
                        onClick={() => {
                          setIsEditing(true)
                          setIsActive(false)
                        }}
                      >
                        {t('commentsEditComment')}
                      </Text>
                      <Text
                        variant="text-small"
                        className={styles.action}
                        onClick={onDeleteComment}
                      >
                        {t('commentsDeleteComment')}
                      </Text>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}
      {isEditing && (
        <form
          className={styles['edit-comment']}
          onSubmit={(e) => onEditComment(e)}
        >
          <Input
            value={editingComment}
            onChange={(value) => setEditingComment(value)}
          />
          <Button variant="danger" onClick={() => setIsEditing(false)}>
            <CloseIcon className={styles.close} />
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={!editingComment}
            isActive={!!editingComment}
          >
            <ChevronRightIcon />
          </Button>
        </form>
      )}
    </div>
  )
}
